import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
    Plus, Sparkles, Loader2, Search, Save, X, Edit3, Power, PowerOff, Check,
    Upload, FileText, Download, PackageOpen, Trash2,
    BookOpen,
} from 'lucide-react';

const VIOLET = '#7C6BEF';
const GREEN = '#10B981';
const AMBER = '#F59E0B';
const RED = '#DC2626';

const EMPTY_FORM = { code: '', name: '', description: '', category: 'Cumplimiento Normativo', is_active: true, plain_explanation: '', explanation_approved: false };
const CATEGORIES = ['Seguridad', 'Salud Ocupacional', 'Calidad', 'Medio Ambiente', 'Cumplimiento Normativo'];

// Iter 55 · Ported from AdminStandards.jsx · quick-import de 12 normas oficiales.
// Se usa como seed masivo cuando el catálogo está vacío o le faltan normas comunes.
const OFFICIAL_CATALOG = [
    { code: 'NOM-001-STPS-2008', name: 'Edificios, locales e instalaciones', category: 'Seguridad', description: 'Condiciones de seguridad en edificios, locales e instalaciones de los centros de trabajo para prevenir riesgos.' },
    { code: 'NOM-002-STPS-2010', name: 'Prevención y protección contra incendios', category: 'Seguridad', description: 'Requisitos para prevenir y controlar incendios en centros de trabajo.' },
    { code: 'NOM-017-STPS-2008', name: 'Equipo de protección personal — Selección, uso y manejo', category: 'Salud Ocupacional', description: 'Requisitos para la selección, uso, manejo, mantenimiento, almacenamiento y disposición del EPP.' },
    { code: 'NOM-026-STPS-2008', name: 'Colores y señales de seguridad', category: 'Seguridad', description: 'Colores y señales de seguridad e higiene, e identificación de riesgos por fluidos conducidos en tuberías.' },
    { code: 'NOM-029-STPS-2011', name: 'Mantenimiento de instalaciones eléctricas', category: 'Seguridad', description: 'Condiciones de seguridad para el mantenimiento de instalaciones eléctricas en los centros de trabajo.' },
    { code: 'NOM-030-STPS-2009', name: 'Servicios preventivos de seguridad y salud', category: 'Salud Ocupacional', description: 'Funciones y actividades para los servicios preventivos de seguridad y salud en el trabajo.' },
    { code: 'NOM-019-STPS-2011', name: 'Comisiones de seguridad e higiene', category: 'Salud Ocupacional', description: 'Constitución, integración y funcionamiento de las comisiones de seguridad e higiene.' },
    { code: 'NOM-035-STPS-2018', name: 'Factores de riesgo psicosocial', category: 'Salud Ocupacional', description: 'Identificación, análisis y prevención de los factores de riesgo psicosocial en el trabajo.' },
    { code: 'ISO 9001:2026', name: 'Sistema de Gestión de Calidad', category: 'Calidad', description: 'Requisitos para un sistema de gestión de calidad enfocado en satisfacción del cliente y mejora continua.' },
    { code: 'ISO 14001:2026', name: 'Sistema de Gestión Ambiental', category: 'Medio Ambiente', description: 'Requisitos para un sistema de gestión ambiental para proteger el medio ambiente y prevenir la contaminación.' },
    { code: 'ISO 45001:2018', name: 'Sistema de Gestión de Seguridad y Salud', category: 'Seguridad', description: 'Requisitos para un sistema de gestión de seguridad y salud en el trabajo (SST).' },
    { code: 'ISO/IEC 27001:2022', name: 'Sistema de Gestión de Seguridad de la Información', category: 'Cumplimiento Normativo', description: 'Requisitos para establecer, implementar y mejorar un sistema de gestión de seguridad de la información.' },
];

// Color de badge por categoría (fondo suave + texto oscuro/vivo). Mismo lenguaje visual que el badge de "Activa".
const CATEGORY_BADGE = {
    'Seguridad':               { bg: 'rgba(239,68,68,0.12)',  fg: '#DC2626' }, // rojo/naranja suave
    'Salud Ocupacional':       { bg: 'rgba(59,130,246,0.12)', fg: '#2563EB' }, // azul suave
    'Calidad':                 { bg: 'rgba(16,185,129,0.12)', fg: '#059669' }, // verde suave
    'Medio Ambiente':          { bg: 'rgba(20,184,166,0.12)', fg: '#0D9488' }, // teal
    'Cumplimiento Normativo':  { bg: 'rgba(124,107,239,0.12)', fg: '#7C6BEF' }, // morado del panel
};
const LEGACY_BADGE = { bg: 'rgba(107,114,128,0.15)', fg: '#6B7280' }; // gris neutro

function CategoryBadge({ category }) {
    const value = (category || '').trim() || '—';
    const isMigrated = Object.prototype.hasOwnProperty.call(CATEGORY_BADGE, value);
    const style = isMigrated ? CATEGORY_BADGE[value] : LEGACY_BADGE;
    return (
        <span
            className="inline-flex items-center px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full whitespace-nowrap"
            style={{ background: style.bg, color: style.fg }}
            title={isMigrated ? value : `Categoría legada · ${value} (edita para migrar)`}
            data-legacy={isMigrated ? undefined : 'true'}
        >
            {value}
        </span>
    );
}

// Biblioteca · busqueda sin acentos ni mayusculas; conserva la longitud para ubicar la seleccion.
function foldTexto(s) {
  let out = '';
  for (let i = 0; i < s.length; i += 1) {
    const c = s[i];
    const b = c.normalize('NFD').charAt(0).toLowerCase().charAt(0);
    out += b || c;
  }
  return out;
}

// Biblioteca (24-oct-2026) · etiqueta de la liga segun la fuente oficial de la ficha.
function fuenteOficial(url) {
  let host = '';
  try { host = new URL(url).hostname.replace(/^www\./, ''); } catch (e) { return 'Fuente oficial'; }
  if (host.endsWith('dof.gob.mx')) return 'Texto en el DOF';
  if (host.endsWith('ecfr.gov')) return 'Texto en el eCFR';
  if (host.endsWith('fda.gov')) return 'Página oficial de la FDA';
  if (host.endsWith('cdc.gov')) return 'Página oficial del CDC';
  if (host.endsWith('nfpa.org')) return 'Página oficial de NFPA';
  if (host.endsWith('iso.org')) return 'Página oficial de ISO';
  return 'Fuente oficial';
}

export default function AdminSettingsCertifications() {
    const [items, setItems] = useState([]);
    // Biblioteca · visor del texto oficial completo
    const [fullText, setFullText] = useState(null); // { std, docs, loading }
    const [ftQuery, setFtQuery] = useState('');
    const ftRef = React.useRef(null);
    const ftPos = React.useRef(0);
  const ftFold = React.useRef({ src: null, txt: '' });
    const openFullText = async (s) => {
      setFtQuery('');
      ftPos.current = 0;
      setFullText({ std: s, docs: [], loading: true });
      try {
        const r = await api.get(`/standards/${s.id}/text`);
        setFullText({ std: s, docs: r.data || [], loading: false });
      } catch (e) {
        setFullText(null);
        toast.error(apiErrorMessage(e, 'No se pudo cargar el texto completo.'));
      }
    };
    const ftFind = () => {
      const ta = ftRef.current;
      const qq = ftQuery.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      if (!ta || !qq) return;
      if (ftFold.current.src !== ta.value) ftFold.current = { src: ta.value, txt: foldTexto(ta.value) };
    const t = ftFold.current.txt;
      let i = t.indexOf(qq, ftPos.current);
      if (i < 0) i = t.indexOf(qq, 0);
      if (i < 0) { toast.error('No se encontró en el texto.'); return; }
      ftPos.current = i + qq.length;
      ta.focus();
      ta.setSelectionRange(i, i + qq.length);
      // medirAltura: copia invisible con el mismo ancho y letra para saber a que altura queda la coincidencia
      const cs = window.getComputedStyle(ta);
      const medirAltura = document.createElement('div');
      ['fontFamily', 'fontSize', 'fontWeight', 'lineHeight', 'letterSpacing', 'paddingTop', 'paddingLeft', 'paddingRight', 'tabSize'].forEach((p) => { medirAltura.style[p] = cs[p]; });
      Object.assign(medirAltura.style, { position: 'absolute', visibility: 'hidden', left: '-99999px', top: '0', boxSizing: 'border-box', width: `${ta.clientWidth}px`, whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordWrap: 'break-word' });
      medirAltura.textContent = ta.value.slice(0, i);
      document.body.appendChild(medirAltura);
      const y = medirAltura.scrollHeight;
      document.body.removeChild(medirAltura);
      ta.scrollTop = Math.max(0, y - ta.clientHeight / 3);
    };
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [showInactive, setShowInactive] = useState(true);
    const [editing, setEditing] = useState(null); // null | { ...standard } | 'new'
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    // AI assist
    const [aiPrompt, setAiPrompt] = useState('');
    const [aiLoading, setAiLoading] = useState(false);
    const [aiPreview, setAiPreview] = useState(null); // { kind: 'proposal', ... } or { kind: 'clarification', message }

    // Iter 55 · Documents per standard (PDF upload feature ported from AdminStandards)
    const [documents, setDocuments] = useState({}); // { [standardId]: [ {id,filename,url,...} ] }
    const [uploadingDoc, setUploadingDoc] = useState(false);

    // Iter 55 · Quick-import de catálogo oficial NOM/ISO (ported)
    const [showImport, setShowImport] = useState(false);
    const [importing, setImporting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('/standards');
            setItems(r.data || []);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al cargar catálogo'));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return items.filter((s) => {
            if (!showInactive && s.is_active === false) return false;
            if (!needle) return true;
            return [s.code, s.name, s.description, s.category]
                .filter(Boolean)
                .join(' ')
                .toLowerCase()
                .includes(needle);
        });
    }, [items, q, showInactive]);

    // Iter 102 · panel visual "Migrar referencias a otra norma"
    const [refItems, setRefItems] = useState(null); // {count, items}
    const [migrateTo, setMigrateTo] = useState('');
    const [migrating, setMigrating] = useState(false);
    const loadRefs = async (id) => {
        try { const r = await api.get(`/standards/${id}/referencing-items`); setRefItems(r.data); }
        catch { setRefItems(null); }
    };
    const migrateRefs = async () => {
        const target = items.find((s) => s.id === migrateTo);
        if (!target) { toast.error('Elige la norma destino.'); return; }
        setMigrating(true);
        try {
            const r = await api.post('/standards/migrate-references', { from_code: editing.code, to_code: target.code });
            toast.success(`Migrados ${r.data.migrated_count} ítem(s) a ${target.code}. Si el conteo llega a 0, ya puedes eliminar esta norma.`);
            await loadRefs(editing.id);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al migrar referencias.');
        } finally { setMigrating(false); }
    };

    const openNew = () => {
        setForm(EMPTY_FORM);
        setAiPrompt('');
        setAiPreview(null);
        setEditing('new');
    };

    const openEdit = (item) => {
        setForm({
            code: item.code || '',
            name: item.name || '',
            description: item.description || '',
            category: item.category || 'General',
            is_active: item.is_active !== false,
            plain_explanation: item.plain_explanation || '',
            explanation_approved: item.explanation_approved === true,
        });
        setAiPrompt('');
        setAiPreview(null);
        setEditing(item);
        // Iter 102 · cargar conteo de ítems que referencian esta norma
        setMigrateTo('');
        setRefItems(null);
        loadRefs(item.id);
        // Iter 55 · load PDF documents attached to this standard
        api.get(`/standards/${item.id}/documents`)
            .then((r) => setDocuments((prev) => ({ ...prev, [item.id]: r.data || [] })))
            .catch(() => setDocuments((prev) => ({ ...prev, [item.id]: [] })));
    };

    // Iter 55 · Upload a PDF to the currently-edited standard.
    const uploadDoc = async (file) => {
        if (!editing || editing === 'new' || !file) return;
        const sid = editing.id;
        setUploadingDoc(true);
        const fd = new FormData();
        fd.append('file', file);
        try {
            const _res = await api.post(`/standards/${sid}/documents`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const _d = _res?.data || {};
            const _pag = _d.num_paginas ? `${_d.num_paginas} pág.` : '';
            const _car = _d.caracteres ? `${Math.round(_d.caracteres / 1000)} mil caracteres` : '';
            const _det = [_pag, _car].filter(Boolean).join(' · ');
            toast.success(
                'Texto oficial cargado a la Biblioteca' + (_det ? ` (${_det})` : '') +
                '. La IA ya puede citar sus numerales.'
            );
            const r = await api.get(`/standards/${sid}/documents`);
            setDocuments((prev) => ({ ...prev, [sid]: r.data || [] }));
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al subir PDF'));
        } finally {
            setUploadingDoc(false);
        }
    };

    // Iter 55 · Quick-import de catálogo oficial. Evita duplicados por code.
    const runQuickImport = async (candidates) => {
        setImporting(true);
        const existingCodes = new Set(items.map((s) => (s.code || '').toLowerCase()));
        const toCreate = candidates.filter((c) => !existingCodes.has(c.code.toLowerCase()));
        let created = 0;
        const failed = [];
        for (const c of toCreate) {
            try {
                await api.post('/standards', {
                    code: c.code, name: c.name, description: c.description,
                    category: c.category, is_active: true,
                });
                created += 1;
            } catch (e) {
                failed.push({ code: c.code, error: apiErrorMessage(e, 'error') });
            }
        }
        setImporting(false);
        if (created > 0) toast.success(`Importadas ${created} normas nuevas · ${OFFICIAL_CATALOG.length - toCreate.length} ya existían`);
        else toast.info('Todas las normas del catálogo ya estaban registradas.');
        if (failed.length) toast.error(`${failed.length} fallaron: ${failed.slice(0,2).map((f)=>f.code).join(', ')}`);
        setShowImport(false);
        await load();
    };

    const closeModal = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setAiPrompt('');
        setAiPreview(null);
    };

    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const askAI = async () => {
        if (!aiPrompt.trim()) {
            toast.error('Escribe la petición para la IA (ej. "agrega ISO 45001 sobre seguridad y salud").');
            return;
        }
        setAiLoading(true);
        setAiPreview(null);
        try {
            const r = await api.post('/admin/certifications-catalog/ai-suggest', { prompt: aiPrompt });
            setAiPreview(r.data);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'La IA no pudo generar una sugerencia.'));
        } finally {
            setAiLoading(false);
        }
    };

    const applyAI = () => {
        if (!aiPreview || aiPreview.kind !== 'proposal') return;
        setForm((p) => ({
            ...p,
            code: aiPreview.code || p.code,
            name: aiPreview.name || p.name,
            description: aiPreview.description || p.description,
            category: aiPreview.category || p.category,
        }));
        toast.success('Propuesta aplicada. Revisa y guarda.');
    };

    const save = async () => {
        if (!form.code.trim() || !form.name.trim()) {
            toast.error('Código y nombre son obligatorios.');
            return;
        }
        const code = form.code.trim();
        if (code !== code.toUpperCase()) {
            toast.error(`El código debe ir en MAYÚSCULAS (ej. ${code.toUpperCase()}).`);
            return;
        }
        if (code.toUpperCase().startsWith('NOM') && !/^NOM-\d{3}-[A-Z0-9]+-\d{4}$/.test(code)) {
            toast.error('Formato de NOM inválido. Debe ser NOM-XXX-XXX-AAAA, ej. NOM-002-STPS-2010.');
            return;
        }
        setSaving(true);
        try {
            if (editing === 'new') {
                await api.post('/standards', {
                    code: form.code.trim(),
                    name: form.name.trim(),
                    description: form.description.trim(),
                    category: form.category,
                    industry: '',
                    approval_threshold: 80,
                });
                toast.success('Normativa agregada.');
            } else {
                await api.patch(`/standards/${editing.id}`, {
                    code: form.code.trim(),
                    name: form.name.trim(),
                    description: form.description.trim(),
                    category: form.category,
                    is_active: form.is_active,
                    plain_explanation: form.plain_explanation.trim(),
                    explanation_approved: form.explanation_approved,
                });
                toast.success('Normativa actualizada.');
            }
            await load();
            closeModal();
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al guardar.'));
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (item) => {
        const nextActive = !(item.is_active !== false);
        const verb = nextActive ? 'reactivar' : 'desactivar';
        if (!window.confirm(`¿Seguro que quieres ${verb} "${item.code}"?`)) return;
        try {
            await api.patch(`/standards/${item.id}`, { is_active: nextActive });
            toast.success(`Normativa ${nextActive ? 'reactivada' : 'desactivada'}.`);
            await load();
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al cambiar estado.'));
        }
    };

    const deleteStandard = async (item) => {
        if (!window.confirm(`¿Eliminar "${item.code}" de la Biblioteca de Normativas? Esta acción no se puede deshacer.`)) return;
        try {
            await api.delete(`/standards/${item.id}`);
            toast.success('Normativa eliminada.');
            await load();
        } catch (e) {
            toast.error(apiErrorMessage(e, 'No se pudo eliminar.'));
        }
    };

    return (
        <AdminLayout title="Ajustes · Biblioteca de Normativas">
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <h2 className="font-display text-2xl font-black tracking-tight" style={{ color: 'var(--fg)' }}>
                            Biblioteca de Normativas
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
                            Referencia técnica de normas individuales (ISO, NOM, NFPA, OSHA, etc.). Se usan para etiquetar items del checklist y como contexto documental para el análisis IA. Para los paquetes vendibles al cliente ve a <strong>Distintivos</strong>.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => (window.location.href = '/admin/settings/citations')}
                            data-testid="settings-cert-goto-citations-btn"
                            className="inline-flex items-center gap-2 px-4 py-2 font-bold text-sm border transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                            style={{ borderColor: '#10B981', color: '#047857' }}
                            title="Las explicaciones para reportes ahora se gestionan por cita exacta"
                        >
                            <Sparkles className="w-4 h-4" /> Explicaciones por cita
                        </button>
                        <button
                            onClick={() => setShowImport(true)}
                            data-testid="settings-cert-quick-import-btn"
                            className="inline-flex items-center gap-2 px-4 py-2 font-bold text-sm border transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                            style={{ borderColor: VIOLET, color: VIOLET }}
                            title="Importar 12 NOMs / ISOs oficiales de un click"
                        >
                            <PackageOpen className="w-4 h-4" /> Importar catálogo NOM oficial
                        </button>
                        <button
                            onClick={openNew}
                            data-testid="settings-cert-add-btn"
                            className="inline-flex items-center gap-2 px-4 py-2 font-bold text-sm text-white transition-opacity hover:opacity-90"
                            style={{ background: VIOLET }}
                        >
                            <Plus className="w-4 h-4" /> Agregar normativa
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="relative flex-1 min-w-[240px] max-w-md">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Buscar por código, nombre o categoría..."
                            data-testid="settings-cert-search"
                            className="w-full pl-9 pr-3 py-2 text-sm border outline-none"
                            style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}
                        />
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={showInactive}
                            onChange={(e) => setShowInactive(e.target.checked)}
                            className="accent-[#7C6BEF]"
                            data-testid="settings-cert-show-inactive"
                        />
                        <span className="text-gray-600 dark:text-gray-300">Mostrar inactivas</span>
                    </label>
                    <span className="text-xs text-gray-400 ml-auto">{filtered.length} de {items.length}</span>
                </div>

                {/* List */}
                <div className="border" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                    {loading ? (
                        <div className="p-12 flex items-center justify-center text-gray-400">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando catálogo...
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-12 text-center text-gray-400 text-sm">Sin normativas que coincidan.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" style={{ tableLayout: 'fixed', minWidth: 880 }}>
                                <colgroup>
                                    <col style={{ width: '16%' }} />{/* Código */}
                                    <col style={{ width: '31%' }} />{/* Nombre + descripción */}
                                    <col style={{ width: '15%' }} />{/* Categoría */}
                                    <col style={{ width: '10%' }} />{/* Estado */}
                                    <col style={{ width: '28%' }} />{/* Acciones */}
                                </colgroup>
                                <thead>
                                    <tr className="border-b text-[10px] uppercase tracking-wider text-gray-500" style={{ borderColor: 'var(--border)' }}>
                                        <th className="text-left px-4 py-3 font-bold">Código</th>
                                        <th className="text-left px-4 py-3 font-bold">Nombre</th>
                                        <th className="text-left px-4 py-3 font-bold">Categoría</th>
                                        <th className="text-left px-4 py-3 font-bold">Estado</th>
                                        <th className="text-right px-4 py-3 font-bold">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((s) => {
                                        const active = s.is_active !== false;
                                        return (
                                            <tr key={s.id} className="border-b last:border-b-0 hover:bg-black/5 dark:hover:bg-white/5 align-top" style={{ borderColor: 'var(--border)' }} data-testid={`settings-cert-row-${s.id}`}>
                                                <td className="px-4 py-4 font-bold truncate" style={{ color: VIOLET }} title={s.code}>{s.code}</td>
                                                <td className="px-4 py-4">
                                                    <div className="truncate font-medium" title={s.name}>{s.name}</div>
                                                    {s.description && <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1" title={s.description}>{s.description}</div>}
                                                    {(s.reference_only || s.dof_date || s.text_chars) && (
                                                      <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5" data-testid={`settings-cert-oficial-${s.id}`}>
                                                        {s.reference_only && <span className="font-semibold">Referencia · norma de pago, sin texto</span>}
                                                        {s.reference_only && s.price_reference && <span>Precio aprox.: {s.price_reference}{s.price_source ? ` (fuente: ${s.price_source})` : ''}</span>}
                                                        {s.reference_only && s.edition_note && <span>{s.edition_note}</span>}
                                                        {s.reference_only && s.license_note && <span className="basis-full">{s.license_note}</span>}
                                                        {s.dof_date && <span>DOF: {String(s.dof_date).split('-').reverse().join('/')}</span>}
                                                        {s.entry_into_force && <span>En vigor: {String(s.entry_into_force).split('-').reverse().join('/')}</span>}
                                                        {s.text_chars ? <span>Texto completo: {Number(s.text_chars).toLocaleString('es-MX')} caracteres</span> : null}
                                                        {(s.aliases || []).length > 0 && <span>También: {s.aliases.join(', ')}</span>}
                                                        {s.official_url && <a href={s.official_url} target="_blank" rel="noreferrer" className="underline" style={{ color: VIOLET }}>{fuenteOficial(s.official_url)}</a>}
                                                        {s.text_chars ? <button type="button" onClick={() => openFullText(s)} className="inline-flex items-center gap-1 underline font-semibold" style={{ color: VIOLET }} data-testid={`settings-cert-fulltext-${s.id}`}><BookOpen className="w-3 h-3" />Leer texto completo</button> : null}
                                                      </div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-4">
                                                    <CategoryBadge category={s.category} />
                                                </td>
                                                <td className="px-4 py-4">
                                                    <span
                                                        className="inline-flex items-center px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full whitespace-nowrap"
                                                        style={{
                                                            background: active ? `${GREEN}22` : `${RED}22`,
                                                            color: active ? GREEN : RED,
                                                        }}
                                                    >
                                                        {active ? 'Activa' : 'Inactiva'}
                                                    </span>
                                                    {s.legal_status && !s.reference_only && <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">{s.legal_status}</div>}
                                                </td>
                                                <td className="px-4 py-4">
<div className="flex flex-wrap justify-end gap-1.5">
                                                    <button
                                                        onClick={() => openEdit(s)}
                                                        data-testid={`settings-cert-edit-${s.id}`}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-gray-200 dark:border-white/15 transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                                                        title="Editar"
                                                    >
                                                        <Edit3 className="w-3.5 h-3.5" /> Editar
                                                    </button>
                                                    <button
                                                        onClick={() => toggleActive(s)}
                                                        data-testid={`settings-cert-toggle-${s.id}`}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-gray-200 dark:border-white/15 transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                                                        style={{ color: active ? RED : GREEN }}
                                                        title={active ? 'Desactivar' : 'Reactivar'}
                                                    >
                                                        {active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                                                        {active ? 'Desactivar' : 'Reactivar'}
                                                    </button>
                                                    <button
                                                        onClick={() => deleteStandard(s)}
                                                        data-testid={`settings-cert-delete-${s.id}`}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-gray-200 dark:border-white/15 transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                                                        style={{ color: RED }}
                                                        title="Eliminar"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" /> Eliminar
                                                    </button>
</div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal editor */}
            {editing && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={closeModal}>
                    <div
                        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto border shadow-2xl"
                        style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}
                        onClick={(e) => e.stopPropagation()}
                        data-testid="settings-cert-modal"
                    >
                        {/* Modal header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 z-10" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                            <h3 className="font-display text-lg font-bold">
                                {editing === 'new' ? 'Agregar normativa' : `Editar · ${editing.code}`}
                            </h3>
                            <button onClick={closeModal} className="p-1 hover:bg-black/10 dark:hover:bg-white/10" data-testid="settings-cert-modal-close">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* AI Assist */}
                        <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border)', background: 'rgba(124,107,239,0.05)' }}>
                            <div className="flex items-center gap-2 mb-2">
                                <Sparkles className="w-4 h-4" style={{ color: VIOLET }} />
                                <span className="text-xs uppercase tracking-wider font-bold" style={{ color: VIOLET }}>Asistente IA · Gemini</span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                Describe la normativa en lenguaje natural y la IA propondrá código, nombre y descripción.
                            </p>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={aiPrompt}
                                    onChange={(e) => setAiPrompt(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); askAI(); } }}
                                    placeholder='Ej. "agrega ISO 45001 sobre seguridad y salud en el trabajo"'
                                    data-testid="settings-cert-ai-prompt"
                                    className="flex-1 px-3 py-2 text-sm border outline-none"
                                    style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}
                                />
                                <button
                                    onClick={askAI}
                                    disabled={aiLoading}
                                    data-testid="settings-cert-ai-submit"
                                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                                    style={{ background: VIOLET }}
                                >
                                    {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                    Sugerir
                                </button>
                            </div>

                            {aiPreview && aiPreview.kind === 'proposal' && (
                                <div className="mt-3 p-3 border text-xs space-y-1" style={{ borderColor: VIOLET, background: 'var(--bg)' }} data-testid="settings-cert-ai-preview">
                                    <div><span className="font-bold text-gray-500">Código:</span> <span className="font-bold" style={{ color: VIOLET }}>{aiPreview.code}</span></div>
                                    <div><span className="font-bold text-gray-500">Nombre:</span> {aiPreview.name}</div>
                                    <div><span className="font-bold text-gray-500">Categoría:</span> {aiPreview.category}</div>
                                    <div><span className="font-bold text-gray-500">Descripción:</span> {aiPreview.description}</div>
                                    <button
                                        onClick={applyAI}
                                        data-testid="settings-cert-ai-apply"
                                        className="mt-2 inline-flex items-center gap-1 px-3 py-1 text-xs font-bold text-white"
                                        style={{ background: GREEN }}
                                    >
                                        <Check className="w-3 h-3" /> Aplicar al formulario
                                    </button>
                                    {Array.isArray(aiPreview.sources) && aiPreview.sources.length > 0 && (
                                        <div className="pt-2 border-t mt-2" style={{ borderColor: 'var(--border)' }}>
                                            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Fuentes web (Tavily)</div>
                                            <div className="flex flex-wrap gap-1.5" data-testid="settings-cert-ai-sources">
                                                {aiPreview.sources.slice(0, 6).map((u, i) => {
                                                    let host = u;
                                                    try { host = new URL(u).hostname.replace('www.', ''); } catch { /* keep url */ }
                                                    return (
                                                        <a key={i} href={u} target="_blank" rel="noopener noreferrer"
                                                            data-testid={`ai-source-${i}`}
                                                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono border hover:underline"
                                                            style={{ borderColor: 'var(--border)', color: VIOLET }}
                                                            title={u}>
                                                            {host.slice(0, 34)}
                                                        </a>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {aiPreview && aiPreview.kind === 'clarification' && (
                                <div className="mt-3 p-3 border text-xs" style={{ borderColor: AMBER, background: `${AMBER}12` }} data-testid="settings-cert-ai-clarification">
                                    <div className="flex items-start gap-2">
                                        <Sparkles className="w-4 h-4 shrink-0 mt-0.5" style={{ color: AMBER }} />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-[10px] uppercase tracking-wider mb-1" style={{ color: AMBER }}>La IA necesita más detalle</div>
                                            <div className="whitespace-pre-wrap leading-relaxed">{aiPreview.message}</div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Form */}
                        <div className="px-6 py-4 space-y-4">
                            <div>
                                <label className="text-xs uppercase tracking-wider font-bold text-gray-500 block mb-1">Código *</label>
                                <input
                                    type="text"
                                    value={form.code}
                                    onChange={(e) => set('code', e.target.value)}
                                    placeholder="Ej. ISO 9001:2015"
                                    data-testid="settings-cert-form-code"
                                    className="w-full px-3 py-2 text-sm border outline-none"
                                    style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}
                                />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-wider font-bold text-gray-500 block mb-1">Nombre corto *</label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={(e) => set('name', e.target.value)}
                                    placeholder="Ej. Sistemas de gestión de la calidad"
                                    data-testid="settings-cert-form-name"
                                    className="w-full px-3 py-2 text-sm border outline-none"
                                    style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}
                                />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-wider font-bold text-gray-500 block mb-1">Descripción</label>
                                <textarea
                                    value={form.description}
                                    onChange={(e) => set('description', e.target.value)}
                                    placeholder="Descripción breve del alcance de la norma..."
                                    rows={3}
                                    data-testid="settings-cert-form-description"
                                    className="w-full px-3 py-2 text-sm border outline-none resize-none"
                                    style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-bold text-gray-500 block mb-1">Categoría</label>
                                    <select
                                        value={form.category}
                                        onChange={(e) => set('category', e.target.value)}
                                        data-testid="settings-cert-form-category"
                                        className="w-full px-3 py-2 text-sm border outline-none"
                                        style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}
                                    >
                                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                {editing !== 'new' && (
                                    <div>
                                        <label className="text-xs uppercase tracking-wider font-bold text-gray-500 block mb-1">Estado</label>
                                        <label className="inline-flex items-center gap-2 h-[38px] cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={form.is_active}
                                                onChange={(e) => set('is_active', e.target.checked)}
                                                data-testid="settings-cert-form-active"
                                                className="accent-[#7C6BEF] w-4 h-4"
                                            />
                                            <span className="text-sm">{form.is_active ? 'Activa (visible)' : 'Inactiva (oculta)'}</span>
                                        </label>
                                    </div>
                                )}
                            </div>

                            {/* Iter 102 · Migrar referencias a otra norma (limpieza de entradas corruptas/duplicadas) */}
                            {editing !== 'new' && refItems && (
                                <div className="mt-6 pt-4 border-t" style={{ borderColor: 'var(--border)' }} data-testid="norm-migrate-panel">
                                    <label className="text-xs uppercase tracking-wider font-bold text-gray-500">Migrar referencias a otra norma</label>
                                    <p className="text-[11px] text-gray-500 mt-1 mb-2">
                                        Ítems del checklist maestro que citan esta norma:{' '}
                                        <span className="font-bold" data-testid="norm-ref-count" style={{ color: refItems.count > 0 ? '#B45309' : '#047857' }}>
                                            {refItems.count}
                                        </span>
                                        {refItems.count > 0 && ' — reasígnalos a la norma correcta para poder eliminar esta entrada.'}
                                        {refItems.count === 0 && ' — no está en uso; puedes eliminarla con seguridad.'}
                                    </p>
                                    {refItems.count > 0 && (
                                        <>
                                            <div className="max-h-28 overflow-auto text-[11px] text-gray-500 border mb-2 p-2" style={{ borderColor: 'var(--border)' }} data-testid="norm-ref-list">
                                                {refItems.items.map((it) => (
                                                    <div key={it.item_id} className="truncate">• <b>{it.item_id}</b> — {it.standard_reference || it.legal_framework}</div>
                                                ))}
                                            </div>
                                            <div className="flex flex-wrap items-center gap-2">
                                                <select value={migrateTo} onChange={(e) => setMigrateTo(e.target.value)}
                                                    data-testid="norm-migrate-select"
                                                    className="px-3 py-2 text-sm border outline-none" style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}>
                                                    <option value="">Norma destino…</option>
                                                    {items.filter((s) => s.id !== editing.id && (s.code || '').trim()).map((s) => (
                                                        <option key={s.id} value={s.id}>{s.code} — {s.name || 'sin nombre'}</option>
                                                    ))}
                                                </select>
                                                <button type="button" onClick={migrateRefs} disabled={migrating || !migrateTo}
                                                    data-testid="norm-migrate-btn"
                                                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white disabled:opacity-40" style={{ background: '#7C6BEF' }}>
                                                    {migrating ? 'Migrando…' : 'Migrar referencias'}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Iter 105 · Las explicaciones para reportes se movieron a nivel de CITA exacta */}
                            {editing !== 'new' && (
                                <div className="mt-6 pt-4 border-t" style={{ borderColor: 'var(--border)' }} data-testid="norm-explanation-moved-note">
                                    <label className="text-xs uppercase tracking-wider font-bold text-gray-500">
                                        Explicación para reportes
                                    </label>
                                    <p className="text-[11px] text-gray-500 mt-1">
                                        Las explicaciones en lenguaje claro que aparecen en el PDF/correo ahora se
                                        gestionan por <b>cita exacta</b> del checklist (no por norma), porque una cita
                                        puede combinar varias normas. Ve a{' '}
                                        <a href="/admin/settings/citations" className="font-bold underline" style={{ color: VIOLET }} data-testid="norm-goto-citations-link">
                                            Explicaciones por cita
                                        </a>.
                                    </p>
                                </div>
                            )}

                            {/* Iter 55 · PDF documents attached to this norm (ported feature) */}
                            {editing !== 'new' && (
                                <div className="mt-6 pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-xs uppercase tracking-wider font-bold text-gray-500">
                                            Documentos PDF adjuntos
                                        </label>
                                        <label
                                            data-testid="settings-cert-upload-pdf"
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border cursor-pointer hover:bg-black/5 dark:hover:bg-white/10"
                                            style={{ borderColor: VIOLET, color: VIOLET }}
                                        >
                                            {uploadingDoc ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                                            {uploadingDoc ? 'Subiendo...' : 'Subir PDF'}
                                            <input
                                                type="file"
                                                accept="application/pdf"
                                                className="hidden"
                                                disabled={uploadingDoc}
                                                onChange={(e) => {
                                                    const f = e.target.files && e.target.files[0];
                                                    if (f) uploadDoc(f);
                                                    e.target.value = '';
                                                }}
                                            />
                                        </label>
                                    </div>
                                    {(documents[editing.id] || []).length === 0 ? (
                                        <div className="text-xs text-gray-400 italic py-2">
                                            Ningún PDF adjunto todavía. Sube uno para que la IA lo use como contexto en checklists y hallazgos.
                                        </div>
                                    ) : (
                                        <ul className="space-y-1">
                                            {(documents[editing.id] || []).map((d) => (
                                                <li
                                                    key={d.id}
                                                    data-testid={`settings-cert-doc-${d.id}`}
                                                    className="flex items-center gap-2 px-2 py-1.5 text-xs border"
                                                    style={{ borderColor: 'var(--border)' }}
                                                >
                                                    <FileText className="w-3.5 h-3.5 shrink-0" style={{ color: VIOLET }} />
                                                    <span className="flex-1 truncate" title={d.name || d.filename}>{d.name || d.filename || 'documento.pdf'}</span>
                                                    <span className="text-[10px] opacity-60 shrink-0">
                                                        {d.size ? `${Math.round(d.size / 1024)} KB` : ''}
                                                    </span>
                                                    {(d.url || d.storage_path) && (
                                                        <a
                                                            href={d.url || `/api/storage/${d.storage_path}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="p-1 hover:bg-black/10 dark:hover:bg-white/10"
                                                            title="Descargar"
                                                        >
                                                            <Download className="w-3.5 h-3.5" />
                                                        </a>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: 'var(--border)' }}>
                            <button
                                onClick={closeModal}
                                className="px-4 py-2 text-sm font-medium border hover:bg-black/5 dark:hover:bg-white/10"
                                style={{ borderColor: 'var(--border)' }}
                                data-testid="settings-cert-modal-cancel"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={save}
                                disabled={saving || !form.code.trim() || !form.name.trim()}
                                data-testid="settings-cert-modal-save"
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                                style={{ background: VIOLET }}
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                {editing === 'new' ? 'Crear' : 'Guardar cambios'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Iter 55 · Quick-import modal · catálogo NOM/ISO oficial */}
            {showImport && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    style={{ background: 'rgba(0,0,0,0.6)' }}
                    onClick={() => !importing && setShowImport(false)}
                    data-testid="settings-cert-import-modal"
                >
                    <div
                        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto border shadow-2xl"
                        style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 z-10" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                            <div>
                                <h3 className="font-display text-lg font-bold flex items-center gap-2">
                                    <PackageOpen className="w-5 h-5" style={{ color: VIOLET }} />
                                    Importar catálogo NOM/ISO oficial
                                </h3>
                                <p className="text-xs text-gray-500 mt-1">
                                    12 normas comunes en México. Las que ya existan en tu catálogo (mismo código) serán omitidas.
                                </p>
                            </div>
                            <button
                                onClick={() => setShowImport(false)}
                                disabled={importing}
                                className="p-1 hover:bg-black/10 dark:hover:bg-white/10 disabled:opacity-40"
                                data-testid="settings-cert-import-close"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="px-6 py-4">
                            <ul className="space-y-1.5 max-h-[50vh] overflow-y-auto">
                                {OFFICIAL_CATALOG.map((c) => {
                                    const exists = items.some((s) => (s.code || '').toLowerCase() === c.code.toLowerCase());
                                    return (
                                        <li
                                            key={c.code}
                                            data-testid={`settings-cert-import-row-${c.code}`}
                                            className="flex items-center gap-3 px-3 py-2 border text-sm"
                                            style={{
                                                borderColor: 'var(--border)',
                                                background: exists ? 'rgba(16,185,129,0.05)' : 'transparent',
                                                opacity: exists ? 0.7 : 1,
                                            }}
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="font-bold font-mono text-xs" style={{ color: VIOLET }}>{c.code}</div>
                                                <div className="text-xs opacity-80 truncate" title={c.name}>{c.name}</div>
                                                <div className="text-[10px] uppercase tracking-wider opacity-60 mt-0.5">{c.category}</div>
                                            </div>
                                            {exists ? (
                                                <span
                                                    className="text-[10px] font-bold px-2 py-0.5 inline-flex items-center gap-1"
                                                    style={{ background: `${GREEN}22`, color: GREEN }}
                                                >
                                                    <Check className="w-3 h-3" /> Ya importada
                                                </span>
                                            ) : (
                                                <span
                                                    className="text-[10px] font-bold px-2 py-0.5"
                                                    style={{ background: `${VIOLET}22`, color: VIOLET }}
                                                >
                                                    Pendiente
                                                </span>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>

                        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: 'var(--border)' }}>
                            <button
                                onClick={() => setShowImport(false)}
                                disabled={importing}
                                className="px-4 py-2 text-sm font-medium border hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-40"
                                style={{ borderColor: 'var(--border)' }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => runQuickImport(OFFICIAL_CATALOG)}
                                disabled={importing}
                                data-testid="settings-cert-import-run"
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                                style={{ background: VIOLET }}
                            >
                                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageOpen className="w-4 h-4" />}
                                {importing ? 'Importando...' : 'Importar pendientes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
          {/* Biblioteca · visor del texto oficial completo */}
          {fullText && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={() => setFullText(null)} data-testid="settings-cert-fulltext-modal">
              <div className="w-full max-w-5xl h-[92vh] flex flex-col border shadow-2xl" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3 px-4 sm:px-6 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
                  <div className="min-w-0">
                    <h3 className="font-display text-lg font-bold truncate">{fullText.std.code} · Texto oficial completo</h3>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex flex-wrap gap-x-3">
                      {fullText.docs.map((d) => <span key={d.id || d.name}>{d.name}</span>)}
                      {fullText.std.official_url && <a href={fullText.std.official_url} target="_blank" rel="noreferrer" className="underline" style={{ color: VIOLET }}>{fuenteOficial(fullText.std.official_url)}</a>}
                    </div>
                  </div>
                  <button onClick={() => setFullText(null)} className="p-1 hover:bg-black/10 dark:hover:bg-white/10" data-testid="settings-cert-fulltext-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="flex items-center gap-2 px-4 sm:px-6 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
                  <input value={ftQuery} onChange={(e) => { setFtQuery(e.target.value); ftPos.current = 0; }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ftFind(); } }} placeholder="Buscar en el texto (Enter = siguiente)" className="flex-1 min-w-0 px-3 py-2 text-sm border bg-transparent" style={{ borderColor: 'var(--border)' }} data-testid="settings-cert-fulltext-search" />
                  <button onClick={ftFind} className="px-4 py-2 text-sm font-bold text-white hover:opacity-90" style={{ background: VIOLET }}>Buscar</button>
                </div>
                <div className="flex-1 min-h-0 p-2 sm:p-4">
                  {fullText.loading ? (
                    <div className="h-full flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
                  ) : (
                    <textarea ref={ftRef} readOnly value={fullText.docs.map((d) => d.text).join('\n\n')} className="w-full h-full p-3 text-sm font-mono leading-relaxed border bg-transparent resize-none" style={{ borderColor: 'var(--border)' }} data-testid="settings-cert-fulltext-text" />
                  )}
                </div>
              </div>
            </div>
          )}
        </AdminLayout>
    );
}
